import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

/**
 * Protección general de rutas: toda pantalla exige el permiso del módulo al que pertenece.
 * La URL del módulo es el inicio de la ruta (/rh/colaboradores cubre /rh/colaboradores/perfil/7),
 * como en usePermisos. Si no hay módulo para la ruta se busca en ALIAS (subpantallas cuyo módulo
 * tiene otra URL); las rutas LIBRES no piden permiso. Una ruta sin módulo ni alias solo la abre
 * un administrador (así una pantalla nueva no queda abierta por olvido).
 * Sin sesión no hace nada: el login y el Layout se encargan.
 */

const LIBRES = [
    /^\/$/, /^\/login(\/|$)/, /^\/externos(\/|$)/, /^\/encuesta(\/|$)/, /^\/satisfaccion(\/|$)/, /^\/compartido\//,
    /^\/mi-perfil(\/|$)/, /^\/mi-calendario(\/|$)/, /^\/mis-notificaciones(\/|$)/, /^\/normas(\/|$)/,
    /^\/ti\/soporte(\/|$)/, /^\/ti\/manuales(\/|$)/,
    /^\/rh\/permisos-vacaciones(\/|$)/, // solicitud de vacaciones/permisos del propio colaborador
];

/* Alias solo para la ruta exacta: sus subrutas tienen módulo propio (emitidas ≠ recibidas) */
const ALIAS_EXACTO = {
    '/administracion/facturas-cfdi': ['/administracion/facturas-cfdi/emitidas', '/administracion/facturas-cfdi/recibidas'],
};

/* Alias por prefijo: la ruta y sus subpantallas */
const ALIAS = {
    '/administracion/gastos': ['/administracion/egresos'],
    '/administracion/gasto-compras': ['/administracion/egresos', '/proyectos/compras'],
    '/presupuesto/control-presupuestos': ['/presupuesto/presupuesto', '/presupuesto/utilidad-presupuestos'],
    '/proyectos/clientes': ['/leads/clientes', '/proyectos/proyectos'],
    '/proyectos/ordenes-compra': ['/proyectos/compras', '/proyectos/solicitud-compra'],
    '/catalogos/precio-diseno': ['/catalogos/partidas-diseño', '/presupuesto/presupuesto-diseño'],
    '/catalogos/adjuntos': ['/catalogos/administrativo'],
    '/catalogos/conceptos-facturacion': ['/catalogos/administrativo', '/administracion/facturacion'],
    '/rh/nomina-prestaciones': ['/rh/prestaciones', '/rh/nomina-admin'],
    '/rh/pago-impuestos': ['/rh/nomina-admin'],
    '/rh/impresos': ['/rh/colaboradores'],
    '/rh/operadores': ['/rh/vehiculos', '/rh/solicitudes-vehiculo'],
    '/rh/biblioteca': ['/rh/reuniones'],
    '/rh/minutas': ['/rh/reuniones'],
    '/rh/cursos': ['/rh/reuniones'],
    '/ti/cambio-cliente-proyectos': ['/t/cambio-cliente-proyectos', '/ti/actualizacion-clientes-proyectos'],
};

const normaliza = (url = '') => decodeURIComponent(('/' + String(url).trim()).replace(/\/+/g, '/').replace(/\/$/, '')).toLowerCase() || '/';
const cubre = (moduloUrl, ruta) => ruta === moduloUrl || ruta.startsWith(moduloUrl + '/');

/* true | false | null (sin módulo ni alias) */
export const puedeEntrar = (pathname, permisos = [], esAdmin = false) => {
    const ruta = normaliza(pathname);
    if (LIBRES.some((re) => re.test(ruta))) return true;
    const urls = permisos.map((p) => normaliza(p?.modulo?.url)).filter((u) => u !== '/');
    if (urls.some((u) => cubre(u, ruta))) return true;
    const exacto = Object.keys(ALIAS_EXACTO).find((a) => normaliza(a) === ruta);
    if (exacto) return esAdmin || ALIAS_EXACTO[exacto].some((m) => urls.includes(normaliza(m)));
    const alias = Object.keys(ALIAS).find((a) => cubre(normaliza(a), ruta));
    if (alias) return esAdmin || ALIAS[alias].some((m) => urls.includes(normaliza(m)));
    return esAdmin;
};

export default function GuardiaPermisos({ children }) {
    const location = useLocation();
    const history = useHistory();
    const authUser = useSelector((s) => s.authUser);
    const user = authUser?.user;
    const conSesion = !!authUser?.access_token && !!user?.id;
    const esAdmin = Number(user?.tipo?.id ?? user?.tipo) === 1;

    const permitido = useMemo(
        () => !conSesion || puedeEntrar(location.pathname, user?.permisos || [], esAdmin),
        [conSesion, location.pathname, user?.permisos, esAdmin],
    );

    useEffect(() => {
        if (permitido) return;
        Swal.fire('Sin acceso', 'No tienes permisos para entrar a este módulo.', 'warning');
        history.replace('/');
    }, [permitido, history]);

    // No se pinta la pantalla protegida ni un instante mientras se redirige
    return permitido ? children : null;
}
